import React, {useMemo} from 'react'
import Select, {InputActionMeta} from 'react-select'

export type MultiSelectOption<T extends string | number = string | number> = {
  value: T
  label: string
}

type Props<T extends string | number> = {
  label: string
  options: MultiSelectOption<T>[]
  value: T[]
  onChange: (values: T[]) => void
  placeholder?: string
  disabled?: boolean
  onInputChange?: (inputValue: string) => void
}

function MultiSelectWithAll<T extends string | number>({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select option(s)',
  disabled = false,
  onInputChange,
}: Props<T>) {
  const normalizedOptions = useMemo(
    () =>
      options.map((o) => ({
        ...o,
        label: String(o.label ?? ''),
      })),
    [options]
  )

  const allSelected =
    normalizedOptions.length > 0 && value.length === normalizedOptions.length

  const handleChange = (opts: readonly MultiSelectOption<T>[] | null) => {
    onChange(opts ? opts.map((o) => o.value) : [])
  }

  const handleInputChange = (inputValue: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action === 'input-change' && onInputChange) {
      onInputChange(inputValue)
    }
  }

  return (
    <div className='col-12'>
      <div className='d-flex justify-content-between align-items-center mb-1'>
        <label className='form-label fs-7 mb-0'>{label}</label>
        <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
          <input
            className='form-check-input'
            type='checkbox'
            checked={allSelected}
            disabled={disabled || normalizedOptions.length === 0}
            onChange={(e) => {
              if (e.target.checked) {
                onChange(normalizedOptions.map((o) => o.value))
              } else {
                onChange([])
              }
            }}
          />
          <label className='form-check-label'>All</label>
        </div>
      </div>
      <Select
        isMulti
        isDisabled={disabled}
        classNamePrefix='react-select'
        options={normalizedOptions}
        value={normalizedOptions.filter((o) => value.includes(o.value))}
        onChange={handleChange}
        onInputChange={onInputChange ? handleInputChange : undefined}
        filterOption={onInputChange ? () => true : undefined}
        placeholder={placeholder}
        menuPortalTarget={document.body}
        styles={{menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999})}}
      />
    </div>
  )
}

export default MultiSelectWithAll
